import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import { useCreateAffiliation } from '~/hooks/apis/agent/useCreateAffiliation';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { Affiliation } from '~/types/services/agentHub';

interface Props {
  onClose: () => void;
  isOpen: boolean;
}

export default function AddAffiliationModal({ onClose, isOpen = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const brand = useCurrentBrand();

  const regions = useMemo(() => ['world', ...getRegions(brand).map((region) => region.code)], [brand]);

  const [affiliationDetails, setAffiliationDetails] = useState<Partial<Affiliation>>({});

  // Mutation
  const createAffiliationMutation = useCreateAffiliation({
    onSuccess: () => {
      enqueueSnackbar('Affiliation created successfully');
      setAffiliationDetails({});
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`There was an error creating the affiliation: ${error.message}`, { variant: 'error' });
    },
  });

  const save = useCallback(() => {
    if (!affiliationDetails.name || !affiliationDetails.region) {
      enqueueSnackbar('Please fill all the fields', { variant: 'error' });
      return;
    }

    createAffiliationMutation.mutate({
      name: affiliationDetails.name,
      region: affiliationDetails.region,
    });
  }, [affiliationDetails, enqueueSnackbar, createAffiliationMutation]);

  const handleClose = useCallback(() => {
    setAffiliationDetails({});
    onClose();
  }, [onClose]);

  const isSaving = createAffiliationMutation.isPending;
  const isError = createAffiliationMutation.isError;

  return (
    <Dialog onClose={handleClose} open={isOpen}>
      <DialogTitle>Add Affiliation</DialogTitle>

      <DialogContent>
        {isSaving && <Spinner />}
        {!isSaving && (
          <form onSubmit={(e) => e.preventDefault()} style={{ minWidth: '500px' }}>
            <Stack mt={2} direction="column" spacing={2}>
              <FormGroup>
                <TextField
                  label="Name"
                  value={affiliationDetails?.name ?? ''}
                  required
                  fullWidth
                  onChange={(e) => setAffiliationDetails({ ...affiliationDetails, name: e.target.value })}
                />
              </FormGroup>

              <FormControl>
                <InputLabel id="affiliation-region">Region</InputLabel>
                <Select
                  labelId="affiliation-region"
                  id="affiliation-region"
                  label="Region"
                  required
                  fullWidth
                  value={affiliationDetails?.region ?? ''}
                  onChange={(e) => setAffiliationDetails({ ...affiliationDetails, region: e.target.value })}
                >
                  {regions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isError && <ErrorDisplay message="There was an error creating the affiliation" severity="error" />}
            </Stack>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant="text" onClick={handleClose}>
          Close
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
